import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
} from '@apollo/client';
import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum'; // TODO: SAV-3878 build react-native support

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

function getOperationName(document: DocumentNode) {
  const definition = document.definitions[0];

  if (definition === undefined || !('name' in definition)) {
    return undefined;
  }

  return definition.name?.value;
}

function isAuthorizationError(errorMessage: string) {
  return errorMessage.includes("QueryType's authorization check");
}

/**
 * @deprecated Use useQueryWithErrorLogging from @savvy/graphql-hooks instead
 */
export function useQueryWithErrorLogging<TVariables extends OperationVariables, TData>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
  const resp = useQuery(query, options);
  const advisorId = loggedInAsAdvisorID();
  const { user: authUser, reauth } = useAuth();

  useEffect(() => {
    if (resp.error) {
      try {
        throw new Error(
          `${getOperationName(query)}
          error_name=${resp.error.name} 
          error_message=${resp.error.message}`,
        );
      } catch (error) {
        console.error(error);
        try {
          datadogRum.addError(error, {
            user_id: authUser?.id,
            logged_in_as_user_id: advisorId,
          });
        } catch (rumError) {
          // @datadog/browser-rum is web-only (SAV-3878) and throws on native. Logging here
          // surfaces genuine web-side RUM failures instead of silently swallowing them.
          console.error('[datadog-rum]', rumError);
        }
        if (isAuthorizationError(resp.error.message)) {
          reauth();
        }
      }
    }
  }, [resp.error]);

  return resp;
}
